import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Item, Label, Segment, Grid, List, Popup } from 'semantic-ui-react';
import { Game } from '../../../app/models/Game';
import { format } from 'date-fns';
import GameListItemParticipant from './GameListItemParticipant';
import { GameLocationButton } from '../../../components/map/GameLocationButton';
import GameFollowButton from '../details/GameFollowButton';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { formatDateDisplay } from '../../../app/common/util/dateUtils';

// ─── CTA Discord-style Animation ─────────────────────────────────────────────
const CTA_STYLES = `
  @keyframes cta-shimmer-once {
    0%   { transform: translateX(-100%) skewX(-15deg); opacity: 1; }
    100% { transform: translateX(250%)  skewX(-15deg); opacity: 1; }
  }

  @keyframes cta-arrow-bounce {
    0%, 100% { transform: translateX(0); }
    30%       { transform: translateX(4px); }
    60%       { transform: translateX(-2px); }
  }

  .cta-discord-btn {
    position: relative !important;
    overflow: hidden !important;
    transition: background 0.18s ease, transform 0.15s ease, box-shadow 0.18s ease !important;
  }

  .cta-discord-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    animation: cta-shimmer-once 0.75s ease-out 0.3s 1 both;
    pointer-events: none;
  }

  .cta-discord-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 14px rgba(0, 181, 173, 0.35) !important;
  }

  .cta-discord-btn:active {
    transform: translateY(0px) scale(0.98) !important;
    transition: transform 0.08s ease !important;
  }

  .cta-discord-btn:hover .cta-arrow {
    animation: cta-arrow-bounce 0.5s ease !important;
    display: inline-block;
  }
`;

interface Props {
  game: Game;
}

export default function GameListItem({ game }: Props) {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ─── Mobile detection ──────────────────────────────────────────────────────
  useEffect(() => {
    const checkIfMobile = () => {
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 1024;
      setIsMobile(isTouchDevice && isSmallScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // ─── Localization helpers ──────────────────────────────────────────────────
  const getLocalizedSport = (sportName: string) => {
    const key = `sports.${sportName.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? sportName : translated;
  };

  const getLocalizedGameType = (gameTypeName: string) => {
    const key = `gameTypes.${gameTypeName.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? gameTypeName : translated;
  };

  const getLocalizedPaymentType = (paymentType: string) => {
    const key = `gamePaymentTypes.${paymentType.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? paymentType : translated;
  };

  const getLocalizedInclusion = (inclusionName: string) => {
    const inclusionKey = inclusionName
      .split(' - ')[0]
      .toLowerCase()
      .replace(/\s+/g, '');
    const key = `inclusionTypes.${inclusionKey}`;
    const translated = t(key);
    return translated === key ? inclusionName : translated;
  };

  // ─── Description helpers ───────────────────────────────────────────────────
  const isLongDescription =
    game.gameDescription && game.gameDescription.length > 300;

  const displayDescription =
    isLongDescription && !isExpanded
      ? game.gameDescription.substring(0, 300) + '...'
      : game.gameDescription;

  // ─── Derived flags ─────────────────────────────────────────────────────────
  const isPast = game.isGameInThePast;
  const ctaClass = !isPast ? 'cta-discord-btn' : undefined;

  return (
    <>
      <style>{CTA_STYLES}</style>

      <Segment.Group raised>

        {/* ── Header: sport image + game name + host info ── */}
        <Segment clearing padded style={{ opacity: isPast ? 0.85 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

            {/* Left: sport image + game name */}
            <div style={{ flex: 1 }}>
              <Item.Group>
                <Item>
                  <Item.Image
                    size='small'
                    circular
                    src={
                      `/assets/categoryImages/${game.sport?.sportName}.jpg` ||
                      'assets/user.png'
                    }
                    style={{
                      maxWidth: '120px',
                      maxHeight: '120px',
                      width: '120px',
                      height: '120px',
                      objectFit: 'cover',
                      margin: '0 auto',
                      display: 'block',
                    }}
                  />

                  <Item.Content>
                    <Item.Header
                      as={Link}
                      to={`/games/${game.id}`}
                      style={{ color: '#2185d0', cursor: 'pointer' }}
                    >
                      {game.gameName}
                    </Item.Header>

                    {game.hostUsername && (
                      <Item.Description>
                        {t('gameListItem.hostedBy')}{' '}
                        <strong>
                          <Link to={`/profiles/${game.hostUsername}`}>
                            {game.hostUsername}
                          </Link>
                        </strong>
                      </Item.Description>
                    )}

                    <Item.Extra>
                      {game.isHost && (
                        <Label
                          basic
                          color='orange'
                          size='small'
                          style={{ marginBottom: '5px' }}
                        >
                          <Icon name='star' />
                          {t('gameListItem.youAreHosting')}
                        </Label>
                      )}

                      {game.isGoing && !isPast && (
                        <Label basic color='green' size='small'>
                          <Icon name='check' />
                          {t('gameListItem.youAreGoing')}
                        </Label>
                      )}

                      {game.isGoing && isPast && (
                        <Label basic color='blue' size='small'>
                          <Icon name='check' />
                          {t('gameListItem.youAttended')}
                        </Label>
                      )}

                      {!isPast && (
                        <Label
                          basic
                          color={game.isLocked ? 'red' : 'green'}
                          size='small'
                          style={{ marginLeft: '5px' }}
                        >
                          <Icon name={game.isLocked ? 'lock' : 'unlock'} />
                          {game.isLocked
                            ? t('gameListItem.registrationClosed')
                            : t('gameListItem.openToJoin')}
                        </Label>
                      )}
                    </Item.Extra>
                  </Item.Content>
                </Item>
              </Item.Group>
            </div>

            {/* Right: host avatar */}
            {game.host && (
              <div
                style={{
                  textAlign: 'center',
                  marginLeft: '20px',
                  minWidth: '100px',
                }}
              >
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                  {t('gameListItem.hostedBy')}:
                </div>
                <Link to={`/profiles/${game.host.userID}`}>
                  <Item.Image
                    size='tiny'
                    circular
                    src={game.host.image || 'assets/user.png'}
                    style={{ margin: '0 auto' }}
                  />
                </Link>
                <div
                  style={{
                    fontSize: '11px',
                    marginTop: '3px',
                    maxWidth: '80px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Link
                    to={`/profiles/${game.host.userID}`}
                    style={{ color: '#3498db', textDecoration: 'none' }}
                  >
                    {game.host.fullName}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </Segment>

        {/* ── Game Details ── */}
        <Segment>
          <Grid columns={2} stackable>
            <Grid.Column>
              <List>
                <List.Item>
                  <List.Icon name='clock' color={isPast ? 'grey' : 'teal'} />
                  <List.Content>
                    {formatDateDisplay(game.dateTimeStart!)}{' '}
                    {format(game.dateTimeStart!, 'h:mm aa')}
                    {isPast && (
                      <Label
                        size='tiny'
                        color='grey'
                        horizontal
                        style={{ marginLeft: '5px' }}
                      >
                        {t('gameListItem.completed')}
                      </Label>
                    )}
                  </List.Content>
                </List.Item>

                <List.Item>
                  <List.Icon name='basketball ball' />
                  <List.Content>
                    <strong>{t('gameListItem.sport')}:</strong>{' '}
                    {getLocalizedSport(game.sport?.sportName || '')}
                  </List.Content>
                </List.Item>

                <List.Item>
                  <List.Icon name='users' />
                  <List.Content>
                    <strong>{t('gameListItem.gameType')}:</strong>{' '}
                    {getLocalizedGameType(game.gameType?.gameTypeName || '')}
                  </List.Content>
                </List.Item>
              </List>
            </Grid.Column>

            <Grid.Column>
              <List>
                <List.Item>
                  <List.Icon name='dollar sign' />
                  <List.Content>
                    <strong>{t('gameListItem.payment')}:</strong>{' '}
                    {getLocalizedPaymentType(
                      game.gamePaymentType?.paymentType || ''
                    )}
                    {game.gamePrice > 0 && ` (${game.gamePrice})`}
                  </List.Content>
                </List.Item>

                <List.Item>
                  <List.Icon name='universal access' />
                  <List.Content>
                    <strong>{t('gameListItem.inclusion')}:</strong>{' '}
                    {getLocalizedInclusion(game.inclusion?.name || '')}
                  </List.Content>
                </List.Item>

                <List.Item>
                  <List.Icon name='users' />
                  <List.Content>
                    <strong>{t('gameListItem.capacity')}:</strong>{' '}
                    {game.gameParticipants?.length || 0}/
                    {game.maxNumberOfParticipants}{' '}
                    {t('gameListItem.participants')}
                  </List.Content>
                </List.Item>

                {!isPast && (
                  <List.Item>
                    <List.Icon
                      name={game.isLocked ? 'lock' : 'unlock'}
                      color={game.isLocked ? 'red' : 'green'}
                    />
                    <List.Content>
                      <strong>{t('gameListItem.status')}:</strong>{' '}
                      {game.isLocked
                        ? t('gameListItem.lockedForRegistration')
                        : t('gameListItem.openForRegistration')}
                    </List.Content>
                  </List.Item>
                )}
              </List>
            </Grid.Column>
          </Grid>
        </Segment>

        {/* ── Location & Follow ── */}
        <Segment>
          <Grid columns={2} stackable>
            <Grid.Column>
              <GameLocationButton game={game} />
              <Label size='large' color='green' pointing='left'>
                <Icon name='map marker alternate' />
                {t('gameListItem.distanceToYou')}: {game.distanceToUserInKm} KM
              </Label>
              <Button
                size='small'
                color='teal'
                icon='external alternate'
                content={t('gameListItem.openInGoogleMaps')}
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps?q=${game.latitude},${game.longitude}`,
                    '_blank'
                  )
                }
                style={{ marginTop: '0.5em', display: 'block' }}
              />
            </Grid.Column>

            <Grid.Column textAlign='right'>
              {isPast ? (
                <Popup
                  content={t('gameListItem.gameAlreadyTookPlace')}
                  trigger={
                    <div style={{ display: 'inline-block' }}>
                      <Button disabled size='tiny' color='grey'>
                        <Icon name='clock' /> {t('gameListItem.pastGame')}
                      </Button>
                    </div>
                  }
                />
              ) : (
                <>
                  {game.isLocked && !game.isGoing && (
                    <Popup
                      content={t('gameListItem.gameLockedForRegistrations')}
                      trigger={
                        <span style={{ marginRight: '10px' }}>
                          <Button disabled size='tiny' color='red'>
                            <Icon name='lock' /> {t('gameListItem.locked')}
                          </Button>
                        </span>
                      }
                    />
                  )}
                  <GameFollowButton game={game} />
                </>
              )}
            </Grid.Column>
          </Grid>
        </Segment>

        {/* ── Participants ── */}
        <Segment>
          <h5>
            {isPast
              ? t('gameListItem.attendees')
              : t('gameListItem.Participants')}
          </h5>
          {game.gameParticipants && (
            <GameListItemParticipant participants={game.gameParticipants!} />
          )}
        </Segment>

        {/* ── Description ── */}
        <Segment padded>
          <h5>{t('gameListItem.aboutThisGame')}</h5>
          <div
            style={{
              maxHeight: isExpanded ? 'none' : '120px',
              overflow: 'hidden',
              marginBottom: isLongDescription ? '10px' : '0',
            }}
          >
            <p
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                margin: '0',
              }}
            >
              {displayDescription}
            </p>
          </div>

          {isLongDescription && (
            <Button
              size='tiny'
              basic
              color='teal'
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ marginTop: '8px' }}
            >
              {isExpanded
                ? t('gameListItem.showLess')
                : t('gameListItem.readMore')}
            </Button>
          )}
        </Segment>

        {/* ── Actions ── */}
        {isMobile ? (
          // Mobile: stacked fluid buttons
          <Segment clearing padded style={{ padding: '8px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
              }}
            >
              <Button
                as={Link}
                to={`/games/${game.id}`}
                color={isPast ? 'grey' : 'teal'}
                fluid
                style={{ fontSize: '0.85rem' }}
                className={ctaClass}
              >
                {isPast
                  ? t('gameListItem.viewGameHistory')
                  : t('gameListItem.viewMoreDetails')}
                {' '}<span className='cta-arrow'>→</span>
              </Button>

              <Button
                as={Link}
                to={{
                  pathname: '/createGame',
                  search: `?duplicate=${game.id}`,
                }}
                color='blue'
                basic
                fluid
                content={t('gameListItem.createSimilarGame')}
                icon='copy'
                labelPosition='left'
                style={{ fontSize: '0.85rem' }}
              />
            </div>
          </Segment>
        ) : (
          // Desktop: floated buttons
          <Segment clearing padded>
            <Button
              as={Link}
              to={`/games/${game.id}`}
              color={isPast ? 'grey' : 'teal'}
              floated='right'
              className={ctaClass}
            >
              {isPast
                ? t('gameListItem.viewGameHistory')
                : t('gameListItem.viewMoreDetails')}
              {' '}<span className='cta-arrow'>→</span>
            </Button>

            <Button
              as={Link}
              to={{
                pathname: '/createGame',
                search: `?duplicate=${game.id}`,
              }}
              color='blue'
              floated='left'
              content={t('gameListItem.createSimilarGame')}
              icon='copy'
              labelPosition='left'
            />
          </Segment>
        )}

      </Segment.Group>
    </>
  );
}